import { useLayoutEffect } from "react"
import { useTheme } from "../theme/useTheme"

export default function Logo() {
      const { theme } = useTheme()
      const colours = [
        '#f5f7ff', // 0.97
        '#e9edff', // 0.83
        '#c8d3ff', // 0.78
        '#a6b7ff', // 0.76
        '#7f95ff', // 0.74
        '#6378f6', // 0.73
        '#4f60d6', // 0.69
        '#424fb0', // 0.67
        '#373f8c', // 0.65
        '#2e356f', // 0.63
        '#1a1d3f', // 0.62
        '#e9edff', // 0.59
        '#c8d3ff', // 0.58
        '#a6b7ff', // 0.45
        '#7f95ff', // 0.43
        '#4f60d6', // 0.42
        '#373f8c', // 0.38
        '#1a1d3f', // 0.36
      ]
      // prettier-ignore
      const brand = [
        { id: 0, letter: 's', },
        { id: 1, letter: 't', },
        { id: 2, letter: 'r', },
        { id: 3, letter: 'a', },
        { id: 4, letter: 'n', },
        { id: 5, letter: 'g', },
        { id: 6, letter: 'e', },
        { id: 7, letter: 'w', },
        { id: 8, letter: 'a', },
        { id: 9, letter: 'y', },
        { id: 10, letter: 's', },
      ]
    
      function calculateLuminance(hexColor: string) {
        const r = parseInt(hexColor.substring(1, 3), 16) / 255
        const g = parseInt(hexColor.substring(3, 5), 16) / 255
        const b = parseInt(hexColor.substring(5, 7), 16) / 255
    
        const rLinear =
          r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
        const gLinear =
          g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
        const bLinear =
          b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)
    
        return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear
      }
    
      const lightColourList = colours.filter(
        (color) => calculateLuminance(color) > 0.25
      )
      const darkColourList = colours.filter(
        (color) => calculateLuminance(color) <= 0.75
      )
    
      const colourList = theme === 'light' ? darkColourList : lightColourList
    
      useLayoutEffect(() => {
        const svg = document.querySelector('svg')
        const path = svg?.querySelector('path') as SVGPathElement | null
        const word = document.querySelector('#brand')
        const letters =
          document.querySelectorAll<HTMLSpanElement>('#brand-name span')
    
        if (word && path && svg) {
          let intervalId: number | undefined
    
          const animateLoop = () => {
            intervalId = window.setInterval(() => {
              const randomColourIndex = Math.floor(
                Math.random() * colourList.length
              )
              letters.forEach((letter) => {
                let randomLetterColourIndex = Math.floor(
                  Math.random() * colourList.length
                )
                while (randomLetterColourIndex === randomColourIndex) {
                  randomLetterColourIndex = Math.floor(
                    Math.random() * colourList.length
                  )
                }
                letter.style.transition = 'color 2s ease-in-out'
                letter.style.color = colourList[randomLetterColourIndex]
              })
            }, 1000)
          }
    
          // Start animation immediately on page load
          animateLoop()
    
          return () => {
            if (intervalId) {
              window.clearInterval(intervalId)
            }
          }
        }
      }, [colourList, theme])
      return (
        <div className="flex items-center justify-center gap-4 py-12 sm:py-16 lg:py-20 relative">
          {/* Enhanced ambient glow effect */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 sm:w-48 sm:h-48 lg:w-64 lg:h-64 bg-brand-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 bg-brand-400/30 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          </div>
          
          {/* Floating brand particles */}
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDuration: '2s', animationDelay: '0.2s' }}></div>
          <div className="absolute top-3/4 right-1/4 w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce" style={{ animationDuration: '2.5s', animationDelay: '0.7s' }}></div>
          <div className="absolute bottom-1/4 left-3/4 w-1 h-1 bg-brand-600 rounded-full animate-bounce" style={{ animationDuration: '3s', animationDelay: '1s' }}></div>
          
          <div className="relative flex items-center justify-center gap-4 animate-fade-in-up">
            <img
              width={120}
              height={120}
              src="/tesseract-dark.svg"
              alt="Strangeways"
              className="inline-flex h-16 w-auto items-center justify-center transition-all duration-500 hover:scale-110 hover:rotate-6 dark:hidden animate-spin-slow"
            />
            <img
              width={120}
              height={120}
              src="/tesseract-light.svg"
              alt="Strangeways"
              className="hidden h-16 w-auto items-center justify-center transition-all duration-500 hover:scale-110 hover:rotate-6 dark:inline-flex animate-spin-slow"
            />
            <h1 id="brand-name" className="text-4xl sm:text-5xl lg:text-6xl ibm-plex-sans-700 tracking-tight transition-all duration-500 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {brand.map((letter, index) => (
                <span 
                  className="italic transition-colors duration-500 inline-block animate-fade-in-up" 
                  key={letter.id}
                  style={{ animationDelay: `${String(index * 0.1)}s` }}
                >
                  {letter.letter}
                </span>
              ))}
            </h1>
          </div>
        </div>
      )
}